import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { CognitoIdentityServiceProvider } from "aws-sdk";
import { AttributeType } from "aws-sdk/clients/cognitoidentityserviceprovider";
import React, { useEffect, useState } from "react";
import DetailField from "../Detail/DetailField";
import GenericTable from "../HealthCareReview/GenericTable";
import AdminService from "./AdminService";

interface Props {
  user: CognitoIdentityServiceProvider.UserType;
  allGroups: CognitoIdentityServiceProvider.GroupListType;
  onAddToGroup: (
    user: CognitoIdentityServiceProvider.UserType,
    group: string
  ) => void;
  onRemoveFromGroup: (
    user: CognitoIdentityServiceProvider.UserType,
    group: string
  ) => void;
  onClose: () => void;
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
    "& > *": {
      margin: theme.spacing(0.5),
    },
  },
}));

export default function UserDialog(props: Props) {
  const PAGES = [
    "ActiveEpisodes",
    "AllEpisodes",
    "EhwcQueue",
    "MassAsymptomaticTesting",
    "Vaccination",
    "PowerUser",
    "SuperUser",
  ];

  const classes = useStyles();

  const [pages, setPages] = useState<string[]>();
  const [groups, setGroups] = useState<string[]>([]);
  const [communities, setCommunities] = useState<string[]>([]);
  const [permissions, setPermissions] = useState<string[]>([]);

  useEffect(() => {
    const loadSelectedGroups = async () => {
      if (props.user.Username) {
        const g = await AdminService.getGroupsForUser(props.user.Username);
        setGroups(
          g
            .filter((gx) => PAGES.indexOf(gx.GroupName || "") < 0)
            .map((gx) => gx.GroupName as string)
        );
        setPages(
          g
            .filter((gx) => PAGES.indexOf(gx.GroupName || "") > -1)
            .map((gx) => gx.GroupName as string)
        );
      }
    };

    loadSelectedGroups();
  }, []);

  const addPermissions = () => {
    const lGroups = [];
    for (const community of communities) {
      if (community === "All") {
        if (
          permissions.indexOf("Resident") > -1 ||
          permissions.indexOf("Employee") > -1 ||
          permissions.indexOf("Other") > -1
        ) {
          if (permissions.indexOf("Resident") > -1) {
            if (permissions.indexOf("Read") > -1) {
              lGroups.push(`All-Resident-Read`);
            }
            if (permissions.indexOf("Write") > -1) {
              lGroups.push(`All-Resident`);
            }
          }
          if (permissions.indexOf("Employee") > -1) {
            if (permissions.indexOf("Read") > -1) {
              lGroups.push(`All-Employee-Read`);
            }
            if (permissions.indexOf("Write") > -1) {
              lGroups.push(`All-Employee`);
            }
          }
          if (permissions.indexOf("Other") > -1) {
            if (permissions.indexOf("Read") > -1) {
              lGroups.push(`All-Other-Read`);
            }
            if (permissions.indexOf("Write") > -1) {
              lGroups.push(`All-Other`);
            }
          }
        } else {
          if (permissions.indexOf("Read") > -1) {
            lGroups.push(`AllRead`);
          }
          if (permissions.indexOf("Write") > -1) {
            lGroups.push(`All`);
          }
        }
      } else {
        if (permissions.indexOf("Resident") > -1) {
          if (permissions.indexOf("Read") > -1) {
            lGroups.push(`${community}-Resident-Read`);
          }
          if (permissions.indexOf("Write") > -1) {
            lGroups.push(`${community}-Resident`);
          }
        }
        if (permissions.indexOf("Employee") > -1) {
          if (permissions.indexOf("Read") > -1) {
            lGroups.push(`${community}-Employee-Read`);
          }
          if (permissions.indexOf("Write") > -1) {
            lGroups.push(`${community}-Employee`);
          }
        }
        if (permissions.indexOf("Other") > -1) {
          if (permissions.indexOf("Read") > -1) {
            lGroups.push(`${community}-Other-Read`);
          }
          if (permissions.indexOf("Write") > -1) {
            lGroups.push(`${community}-Other`);
          }
        }
      }
    }

    for (const group of lGroups) {
      props.onAddToGroup(props.user, group);
    }
    setGroups(Array.from(new Set([...groups, ...lGroups])));
  };

  const getChipColor = (pGroup: string) => {
    if (pGroup === "Admin") {
      return "red";
    }

    return "cadetblue";
  };

  const getCommunity = (pGroup: string) => {
    if (pGroup === "Admin") {
      return "";
    }

    return pGroup.substring(0, 3);
  };

  const getLabel = (pGroup: string) => {
    if (pGroup === "Admin") {
      return "Admin";
    }

    if (pGroup.endsWith("Resident") || pGroup.endsWith("Employee")) {
      pGroup = `${pGroup} Write`;
    }
    return pGroup.substring(3, pGroup.length).replaceAll("-", " ");
  };

  const updatePageAccess = (pPages: string[]) => {
    if (pages) {
      // Add
      if (pPages.length > pages.length) {
        const newPage = pPages.find((p) => pages.indexOf(p) < 0);
        if (newPage) {
          props.onAddToGroup(props.user, newPage);
        }
      } else {
        const pageToDelete = pages.find((p) => pPages.indexOf(p) < 0);
        if (pageToDelete) {
          props.onRemoveFromGroup(props.user, pageToDelete);
        }
      }
    }
    setPages([...pPages]);
  };

  const handleDelete = (pGroup: string) => {
    props.onRemoveFromGroup(props.user, pGroup);
    setGroups(groups.filter((g) => g !== pGroup));
  };

  return (
    <React.Fragment>
      <Dialog
        open={true}
        fullWidth={true}
        onClose={props.onClose}
        aria-labelledby="form-dialog-title"
        maxWidth={"md"}
      >
        <DialogTitle>Update User Groups</DialogTitle>
        <DialogContent>
          {groups && (
            <DetailField
              type="multidropdown"
              label="Community"
              labelWidth={3}
              options={[
                "All",
                "ACH",
                "APL",
                "APR",
                "BBV",
                "CCI",
                "CCV",
                "DVF",
                "ELM",
                "ETH",
                "FRV",
                "GSV",
                "HSD",
                "LHN",
                "LPH",
                "MGC",
                "OCV",
                "RWV",
                "SBV",
                "SLN",
                "TCK",
                "TGB",
                "WCD",
                "WRC",
              ]}
              checkedValues={communities}
              onSelectionChange={(val: string[]) => {
                setCommunities([...val]);
              }}
            />
          )}

          <DetailField
            type="multiselect"
            label="Permissions"
            labelWidth={3}
            // options={['Resident', 'Employee','Other', 'Read', 'Write']}
            options={["Resident", "Read", "Write"]}
            checkedValues={permissions}
            onSelectionChange={(val: string[]) => {
              setPermissions([...val]);
            }}
          />

          <Box ml={11}>
            <Button
              onClick={addPermissions}
              color="primary"
              variant="contained"
              size="small"
            >
              Add
            </Button>
            <Box className={classes.root} mt={3} mb={3}>
              {groups.map((g) => (
                <Chip
                  label={`${getCommunity(g)} ${getLabel(g)}`}
                  clickable
                  style={{ backgroundColor: getChipColor(g) }}
                  color="primary"
                  onDelete={() => handleDelete(g)}
                  deleteIcon={<HighlightOffIcon />}
                />
              ))}
            </Box>
          </Box>

          {/* {pages &&
                        <DetailField
                            type="multidropdown"
                            label="Page Access"
                            labelWidth={3}
                            options={PAGES}
                            checkedValues={pages}
                            onSelectionChange={(val: string[]) => { updatePageAccess(val) }}
                        />
                    } */}

          <DetailField
            type="text"
            readOnly
            labelWidth={3}
            label="ID:"
            value={props.user.Username}
          />

          <DetailField
            type="text"
            readOnly
            labelWidth={3}
            label="Enabled:"
            value={props.user.Enabled === true ? "true" : "false"}
          />

          <DetailField
            type="text"
            readOnly
            labelWidth={3}
            label="Status:"
            value={props.user.UserStatus}
          />

          <DetailField
            type="text"
            readOnly
            labelWidth={3}
            label="Enabled:"
            value={props.user.Enabled === true ? "true" : "false"}
          />

          <DetailField
            type="text"
            readOnly
            labelWidth={3}
            label="Create Date:"
            value={props.user.UserCreateDate as any}
          />

          <DetailField
            type="text"
            readOnly
            labelWidth={3}
            label="Modified Date:"
            value={props.user.UserLastModifiedDate as any}
          />
          <Box mt={2} mb={1}>
            <Typography variant="h6">Attributes</Typography>
          </Box>

          <GenericTable<AttributeType, {}>
            data={props.user.Attributes || []}
            columns={[
              {
                title: "Name",
                field: "Name",
                width: 100,
              },
              {
                title: "Value",
                field: "Value",
                width: 100,
              },
            ]}
            optionOverrides={{
              pageSize: 5,
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={props.onClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}
